// Page-specific config only. Machine selection / pin sets / view mode now
// live in store/viewStore.ts (shared across pages).

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { MAX_HISTORY_WINDOW_MS, type DashboardPreset, type TimeRange } from '@/types';
import { trackReducer, type RangeKind, type TrackState } from '@/view-models/rangeTracking';
import {
  chartInteractionReducer,
  type BandSelection,
} from '@/view-models/chartInteractionReducer';

// ── Dashboard page ──────────────────────────────────────────────────────────
export type DashboardLayout = 'cards' | 'table';

interface DashboardStore {
  preset:    DashboardPreset;
  /** Card grid (default, with sparklines, capped at 6 by preset) vs flat
   *  sortable table view (full fleet, no charts). */
  layout:    DashboardLayout;
  setPreset: (p: DashboardPreset) => void;
  setLayout: (l: DashboardLayout) => void;
}

export const useDashboardStore = create<DashboardStore>()(
  persist(
    (set) => ({
      preset:    4,
      layout:    'cards',
      setPreset: (preset) => set({ preset }),
      setLayout: (layout) => set({ layout }),
    }),
    {
      name: 'lc-dashboard-cfg',
      version: 5,
      // v5 dropped the 6-card preset. Persisted state from v4 carrying
      // preset=6 lands on a now-invalid type; coerce to 4.
      migrate: (state: unknown, fromVersion: number) => {
        const s = (state as Partial<DashboardStore>) ?? {};
        if (fromVersion < 5 && (s.preset as number) === 6) {
          return { ...s, preset: 4 } as DashboardStore;
        }
        return s as DashboardStore;
      },
    },
  ),
);

// ── History page ────────────────────────────────────────────────────────────
//
// pinnedRange is the actual timestamp window used by the historian fetch.
// It is set ONCE by setRange() / setCustomRange() / refreshRange() and never
// recomputed during render. This makes the historian queryKey rock-stable —
// no Date.now() in render path, no fetch storm, no pending state.

interface HistoryStore {
  layout:         'stacked' | 'overlaid';
  range:          TimeRange;
  customRange:    { fromMs: number; toMs: number } | null;
  /** Captured at click time; what the historian actually fetches. For a
   *  tracking relative range this slides forward on each tick(). */
  pinnedRange:    { fromMs: number; toMs: number } | null;
  // Refresh model (issue #18). Relative ranges track `now`; the moment the
  // operator zooms/pans (pauseTracking) they freeze; jumpToLive resumes. Custom
  // (absolute) ranges never track. Transient — excluded from persistence.
  tracking:       boolean;
  asOfMs:         number;
  // ── Chart interaction (consolidated from HistoryPage React state) ──────────
  // The single source of truth for the zoom window + on-chart selection. Owned
  // here so the page renders rather than syncing effects. Transient — never
  // persisted (a persisted zoom/band would be stale on reload).
  /** Zoomed sub-window [fromMs, toMs]; null ⇒ follow the data extent. */
  visible:        [number, number] | null;
  /** Drag-selected band + the row that drew it. */
  band:           BandSelection | null;
  /** Pinned single-point timestamp. */
  clicked:        number | null;
  setLayout:      (l: 'stacked' | 'overlaid') => void;
  setRange:       (r: TimeRange) => void;
  setCustomRange: (r: { fromMs: number; toMs: number } | null) => void;
  /** Re-pin the current relative range (manual refresh). */
  refreshRange:   () => void;
  /** Poll cadence — advance a tracking relative range's trailing edge. */
  tick:           () => void;
  /** A zoom/pan happened — pause a tracking range. */
  pauseTracking:  () => void;
  /** Resume + re-pin to the live edge (the in-chart "jump to live" pill). */
  jumpToLive:     () => void;
  /** Commit a settled zoom/pan gesture: set the visible window AND pause a
   *  tracking range (a deliberate zoom freezes the sliding edge). */
  commitVisible:  (range: [number, number] | null) => void;
  /** Re-pin `visible` to the current range's window — null for live (follow the
   *  stream), the pinned window otherwise. Called on a scope change the store
   *  can't see (viewMode). */
  rePinVisible:   () => void;
  setBand:        (band: BandSelection) => void;
  setClicked:     (ts: number | null) => void;
  clearSelection: () => void;
  /** Reset the zoom: for a relative range return to the live edge + resume
   *  tracking; for a custom range return to its fixed window. */
  resetZoom:      () => void;
}

const kindFor = (range: TimeRange): RangeKind => (range === 'custom' ? 'absolute' : 'relative');

const RANGE_MS: Record<Exclude<TimeRange, 'live' | 'custom'>, number> = {
  '1h':  60 * 60 * 1000,
  '6h':  6 * 60 * 60 * 1000,
  '8h':  8 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '72h': 72 * 60 * 60 * 1000,
  // 7d returns with the volume-local lake (issue #15) — DuckDB-binned
  // slices make the wide windows cheap.
  '7d':  7 * 24 * 60 * 60 * 1000,
};

/** Narrow the store to the chart-interaction slice the reducer operates on, so
 *  the reducer never sees (or accidentally rewrites) the store's other fields. */
function slice(s: { visible: [number, number] | null; band: BandSelection | null; clicked: number | null }) {
  return { visible: s.visible, band: s.band, clicked: s.clicked };
}

/** The `visible` window for a freshly (re-)pinned range: live follows the
 *  stream (null → chart auto-ranges to incoming data); a pinned relative or
 *  custom range pins to its exact window so a picker change moves the x-scale. */
function visibleForPin(
  range: TimeRange,
  pinnedRange: { fromMs: number; toMs: number } | null,
): [number, number] | null {
  if (range === 'live' || !pinnedRange) return null;
  return [pinnedRange.fromMs, pinnedRange.toMs];
}

// Default hot-window for range='live'. The historian's 4h hot path
// (raw + in-process Buffer merge) is engineered around this width so
// the chart always has cloud-truth baseline regardless of how long
// the tab has been open. Live NATS ticks splice on top via the merge
// path in useHistoryWindowMerged. See wip-history-high-density-research.md §0.
const LIVE_HOT_WINDOW_MS = 4 * 60 * 60 * 1000;

function pinFor(range: TimeRange, custom: { fromMs: number; toMs: number } | null) {
  if (range === 'live') {
    // Pin a 4h trailing window for the baseline. Snap the edge to a 5-min
    // boundary so RE-ENTERING live (clicking the LIVE pill, or toggling
    // back from a pinned range) reuses the cached historian window instead
    // of refetching — the live store + IDB cache + live ticks cover the
    // fine recent edge via the splice regardless of the snapped edge.
    const SNAP = 5 * 60_000;
    const now = Math.floor(Date.now() / SNAP) * SNAP;
    return { fromMs: now - LIVE_HOT_WINDOW_MS, toMs: now };
  }
  if (range === 'custom') {
    if (!custom) return null;
    // Clamp custom toMs to now so we never request a future window.
    const toMs = Math.min(custom.toMs, Date.now());
    // Enforce the 3-day ceiling on the resolved window. The picker also
    // guards at selection time, but a stale URL / persisted v5 payload
    // could carry a wider span — clamp from the start instead of the
    // end so the operator's "right edge" intent is preserved.
    const fromMs = Math.max(custom.fromMs, toMs - MAX_HISTORY_WINDOW_MS);
    return { fromMs, toMs };
  }
  const now = Date.now();
  return { fromMs: now - RANGE_MS[range as keyof typeof RANGE_MS], toMs: now };
}

export const useHistoryStore = create<HistoryStore>()(
  persist(
    (set, get) => ({
      layout:         'stacked',
      range:          'live',
      customRange:    null,
      pinnedRange:    null,
      tracking:       true,
      asOfMs:         Date.now(),
      visible:        null,
      band:           null,
      clicked:        null,

      setLayout:      (layout) => set({ layout }),

      setRange: (range) => {
        const { customRange } = get();
        const now = Date.now();
        const next = trackReducer(
          { kind: kindFor(range), tracking: get().tracking, asOfMs: get().asOfMs },
          { type: 'setRange', kind: kindFor(range), nowMs: now },
        );
        const pinnedRange = pinFor(range, customRange);
        // A range change re-pins the zoom to the new window and clears any
        // stale selection carried over from the previous range.
        set({
          range, pinnedRange, tracking: next.tracking, asOfMs: next.asOfMs,
          visible: visibleForPin(range, pinnedRange), band: null, clicked: null,
        });
      },

      setCustomRange: (customRange) => {
        const { range } = get();
        // Switching to custom implicitly when user provides a range.
        const newRange: TimeRange = customRange ? 'custom' : range === 'custom' ? 'live' : range;
        const now = Date.now();
        const next = trackReducer(
          { kind: kindFor(newRange), tracking: get().tracking, asOfMs: get().asOfMs },
          { type: 'setRange', kind: kindFor(newRange), nowMs: now },
        );
        const pinnedRange = pinFor(newRange, customRange);
        set({
          customRange,
          range: newRange,
          pinnedRange,
          tracking: next.tracking,
          asOfMs: next.asOfMs,
          visible: visibleForPin(newRange, pinnedRange), band: null, clicked: null,
        });
      },

      refreshRange: () => {
        const { range, customRange } = get();
        const pinnedRange = pinFor(range, customRange);
        set({ pinnedRange, asOfMs: Date.now(), visible: visibleForPin(range, pinnedRange) });
      },

      tick: () => {
        const { range, tracking, asOfMs, customRange } = get();
        const now = Date.now();
        const next = trackReducer(
          { kind: kindFor(range), tracking, asOfMs },
          { type: 'tick', nowMs: now },
        );
        if (next.asOfMs === asOfMs) return; // no advance (paused / absolute / live no-op handled below)
        // 'live' tracks via the stream + 5-min-snapped window, not pinnedRange;
        // only the pinned relative ranges slide their fetch window here.
        const pinnedRange = range === 'live' ? get().pinnedRange : pinFor(range, customRange);
        // Tracking-follow: glue the zoom window to the advancing pinned window so
        // the chart shows the intended span, not the padded fetch extent. Live
        // keeps visible=null (follows the stream).
        set({ asOfMs: next.asOfMs, pinnedRange, visible: visibleForPin(range, pinnedRange) });
      },

      pauseTracking: () => {
        const { range, tracking, asOfMs } = get();
        const next = trackReducer({ kind: kindFor(range), tracking, asOfMs }, { type: 'gesture' });
        if (next.tracking !== tracking) set({ tracking: next.tracking });
      },

      jumpToLive: () => {
        const { range, tracking, asOfMs, customRange } = get();
        const now = Date.now();
        const next = trackReducer(
          { kind: kindFor(range), tracking, asOfMs },
          { type: 'jumpToLive', nowMs: now },
        );
        if (!next.tracking) return; // absolute — nothing to resume
        const pinnedRange = range === 'live' ? get().pinnedRange : pinFor(range, customRange);
        set({
          tracking: true,
          asOfMs: next.asOfMs,
          pinnedRange,
          // Live returns to stream-follow (null); a relative range re-pins to
          // its live-edge window.
          visible: visibleForPin(range, pinnedRange),
        });
      },

      commitVisible: (range) => {
        // A settled gesture pauses a tracking range, then records the window.
        const { range: r, tracking, asOfMs } = get();
        const next = trackReducer({ kind: kindFor(r), tracking, asOfMs }, { type: 'gesture' });
        const s = chartInteractionReducer(slice(get()), { type: 'setVisible', visible: range });
        set({ tracking: next.tracking, visible: s.visible });
      },

      rePinVisible: () => {
        const { range, pinnedRange } = get();
        set({ visible: visibleForPin(range, pinnedRange) });
      },

      setBand:  (band) => set(chartInteractionReducer(slice(get()), { type: 'setBand', band })),
      setClicked: (ts) => set(chartInteractionReducer(slice(get()), { type: 'setClicked', ts })),
      clearSelection: () => set(chartInteractionReducer(slice(get()), { type: 'clearSelection' })),

      resetZoom: () => {
        const { range } = get();
        if (range !== 'custom') { get().jumpToLive(); return; }
        // Custom (absolute) range: no live edge — return to the fixed window.
        get().rePinVisible();
      },
    }),
    {
      name:    'lc-history-cfg',
      version: 6,
      // tracking/asOfMs + visible/band/clicked are transient (a persisted zoom
      // or asOfMs would be stale on reload); setRange on hydrate re-derives the
      // tracking side. Persist only config.
      partialize: (s) => ({
        layout: s.layout, range: s.range, customRange: s.customRange, pinnedRange: s.pinnedRange,
      }) as HistoryStore,
      // v4 → v5: dropped '7d', added '72h'.
      // v5 → v6: quick presets became live/1h/8h/24h. Migrate a persisted
      // relative range that no longer has a preset to the nearest surviving one
      // ('6h'→'8h', '72h'/'7d'→'24h'); longer windows are reachable via the
      // custom picker. Also clamp any persisted customRange span past the cap.
      migrate: (state: unknown, fromVersion: number): HistoryStore => {
        const s = (state as Partial<HistoryStore>) ?? ({} as Partial<HistoryStore>);
        let range = (s.range as TimeRange | undefined) ?? 'live';
        if (fromVersion < 5 && (range as unknown) === '7d') range = '72h';
        if (fromVersion < 6) {
          if (range === '6h') range = '8h';
          else if (range === '72h' || (range as unknown) === '7d') range = '24h';
        }
        let customRange = s.customRange ?? null;
        if (customRange && customRange.toMs - customRange.fromMs > MAX_HISTORY_WINDOW_MS) {
          customRange = {
            fromMs: customRange.toMs - MAX_HISTORY_WINDOW_MS,
            toMs:   customRange.toMs,
          };
        }
        return {
          ...(s as HistoryStore),
          range,
          customRange,
          pinnedRange: pinFor(range, customRange),
        };
      },
    },
  ),
);
